import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  X, Trash2, Calendar, Tag, AlignLeft, Image as ImageIcon, Users, MessageSquare,
  Move, Camera, Upload, MoreVertical, Crown, Star, Check,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import RichTextEditor from "./RichTextEditor";
import UploadProgress from "./UploadProgress";
import MoveCardMenu from "./MoveCardMenu";

export default function CardModal({ card, board, lists, labels, members, myRole, onClose, onChanged }) {
  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description || "");
  const [dueDate, setDueDate] = useState(card.due_date || "");
  const [cardLabels, setCardLabels] = useState(card.label_ids || []);
  const [cardMembers, setCardMembers] = useState(card.members || []);
  const [attachments, setAttachments] = useState(card.attachments || []);
  const [coverId, setCoverId] = useState(card.cover_attachment_id || null);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [savingDesc, setSavingDesc] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [showMembersPicker, setShowMembersPicker] = useState(false);
  const [showLabelsPicker, setShowLabelsPicker] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const descSaveTimer = useRef(null);

  const isMember = myRole === "member";
  const canEdit = myRole !== "viewer";
  const isMine = card.created_by === card.created_by_actor || true; // we'll derive below
  const isOwn = card.created_by && card.created_by_actor;

  // Load comments
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/cards/${card.card_id}/comments`);
        setComments(r.data);
      } catch {}
    })();
  }, [card.card_id]);

  // Auto-save title on blur
  const saveTitle = async () => {
    if (title === card.title) return;
    try { await api.patch(`/cards/${card.card_id}`, { title }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Auto-save description (debounce)
  const onDescChange = (html) => {
    setDescription(html);
    if (descSaveTimer.current) clearTimeout(descSaveTimer.current);
    descSaveTimer.current = setTimeout(async () => {
      setSavingDesc(true);
      try { await api.patch(`/cards/${card.card_id}`, { description: html }); }
      catch (e) { toast.error("Gagal simpan deskripsi"); }
      finally { setSavingDesc(false); }
    }, 800);
  };

  const saveDueDate = async (val) => {
    setDueDate(val);
    try { await api.patch(`/cards/${card.card_id}`, { due_date: val || null }); onChanged(); }
    catch (e) { toast.error("Gagal"); }
  };

  const toggleLabel = async (labelId) => {
    try {
      const r = await api.post(`/cards/${card.card_id}/labels`, { label_id: labelId });
      if (r.data.attached) setCardLabels((x) => [...x, labelId]);
      else setCardLabels((x) => x.filter((id) => id !== labelId));
      onChanged();
    } catch (e) { toast.error("Gagal"); }
  };

  const toggleMember = async (userId) => {
    try {
      const r = await api.post(`/cards/${card.card_id}/members`, { user_id: userId });
      if (r.data.assigned) setCardMembers((x) => [...x, r.data.user]);
      else setCardMembers((x) => x.filter((m) => m.user_id !== userId));
      onChanged();
    } catch (e) { toast.error("Gagal"); }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploadingFile(true);
    setUploadProgress(0);
    setUploadStatus("uploading");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/cards/${card.card_id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress((e.loaded / e.total) * 100);
        },
      });
      setUploadProgress(100);
      setUploadStatus("success");
      setAttachments((a) => [...a, r.data]);
      if (!coverId) setCoverId(r.data.attachment_id);
      toast.success("Gambar diunggah");
      onChanged();
      setTimeout(() => setUploadingFile(false), 700);
    } catch (e) {
      setUploadStatus("error");
      toast.error(e?.response?.data?.detail || "Gagal upload");
      setTimeout(() => setUploadingFile(false), 1500);
    }
  };

  const onDropFiles = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files[0]) handleFile(files[0]);
  };

  const setCover = async (attId) => {
    try {
      await api.post(`/cards/${card.card_id}/cover/${attId || "none"}`);
      setCoverId(attId);
      onChanged();
    } catch { toast.error("Gagal"); }
  };

  const deleteAttachment = async (attId) => {
    if (!window.confirm("Hapus gambar?")) return;
    try {
      await api.delete(`/attachments/${attId}`);
      setAttachments((a) => a.filter((x) => x.attachment_id !== attId));
      if (coverId === attId) setCoverId(null);
      onChanged();
    } catch { toast.error("Gagal"); }
  };

  const addComment = async () => {
    if (!commentBody.trim() || commentBody === "<p></p>") return;
    try {
      const r = await api.post(`/cards/${card.card_id}/comments`, { body: commentBody });
      setComments((c) => [...c, r.data]);
      setCommentBody("");
    } catch (e) { toast.error("Gagal komen"); }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Hapus komentar?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      setComments((c) => c.filter((x) => x.comment_id !== cid));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const moveToList = async (targetListId) => {
    try {
      await api.post("/cards/move", { card_id: card.card_id, target_list_id: targetListId, new_position: 9999 });
      toast.success("Kartu dipindahkan");
      onChanged();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const deleteCard = async () => {
    if (!window.confirm("Hapus kartu permanen?")) return;
    try {
      await api.delete(`/cards/${card.card_id}`);
      toast.success("Kartu dihapus");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    }
  };

  const currentList = lists.find((l) => l.list_id === card.list_id);
  const coverAtt = attachments.find((a) => a.attachment_id === coverId);

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-2 md:p-4 animate-fadeIn" data-testid="card-modal"
      onDragOver={(e) => e.preventDefault()} onDrop={onDropFiles}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-4xl bg-white border-2 border-black shadow-brutLg max-h-[94dvh] md:max-h-[92vh] overflow-y-auto animate-blockIn">

        {/* Cover preview */}
        {coverAtt && (
          <div className="h-48 border-b-2 border-black overflow-hidden bg-black">
            <img src={coverAtt.url} alt=""
              onClick={() => onOpenLightbox && onOpenLightbox(coverAtt.url)}
              className="w-full h-full object-cover cursor-zoom-in" />
          </div>
        )}

        {/* Header */}
        <div className="border-b-2 border-black p-3 md:p-5 bg-[#F9F9F9] flex items-start justify-between gap-3 md:gap-4 sticky top-0 z-10">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="font-mono text-[10px] uppercase tracking-widest text-black/60">
                [ KARTU / {card.card_id.slice(-6).toUpperCase()} ]
              </div>
              {currentList && (
                <div className="font-mono text-[10px] uppercase tracking-widest bg-black text-white px-2 py-0.5">{currentList.title}</div>
              )}
              {card.on_behalf_of && (
                <div className="font-mono text-[9px] uppercase tracking-widest bg-canary border border-black px-2 py-0.5">
                  Dibuat atas nama: {card.on_behalf_of.for_name}
                </div>
              )}
            </div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle}
              data-testid="card-title-input" disabled={!canEdit}
              className="w-full bg-transparent font-heading font-black text-xl md:text-2xl uppercase tracking-tight border-b-2 border-transparent hover:border-black focus:border-klein transition-colors disabled:cursor-not-allowed" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {canEdit && (
              <div className="relative">
                <button onClick={() => setShowMoveMenu(!showMoveMenu)} data-testid="move-card-btn"
                  title="Pindah" className="w-10 h-10 border border-black bg-white hover:bg-canary transition-colors">
                  <Move size={14} className="mx-auto" strokeWidth={2.5} />
                </button>
                {showMoveMenu && (
                  <MoveCardMenu currentListId={card.list_id}
                    onMove={(targetListId) => moveToList(targetListId)}
                    onClose={() => setShowMoveMenu(false)} />
                )}
              </div>
            )}
            <button onClick={onClose} data-testid="close-card-modal"
              className="w-10 h-10 border border-black bg-white hover:bg-black hover:text-white transition-colors">
              <X size={16} className="mx-auto" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-0">
          {/* MAIN */}
          <div className="p-3 md:p-5 space-y-6 min-w-0">
            {/* Members + labels chips row */}
            <div className="flex flex-wrap gap-4">
              {cardMembers.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1">
                    <Users size={11} strokeWidth={2.5} /> Anggota
                  </div>
                  <div className="flex -space-x-1.5">
                    {cardMembers.map((m) => (
                      <Avatar key={m.user_id} user={m} size={28} title={m.name} />
                    ))}
                  </div>
                </div>
              )}
              {cardLabels.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1">
                    <Tag size={11} strokeWidth={2.5} /> Label
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {cardLabels.map((id) => {
                      const lbl = labels.find((x) => x.label_id === id);
                      if (!lbl) return null;
                      return (
                        <span key={id} className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border-2 border-black"
                          style={{ background: lbl.color, color: getContrast(lbl.color) }}>{lbl.name}</span>
                      );
                    })}
                  </div>
                </div>
              )}
              {dueDate && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest mb-1.5 flex items-center gap-1">
                    <Calendar size={11} strokeWidth={2.5} /> Tenggat
                  </div>
                  <input type="date" value={dueDate} onChange={(e) => saveDueDate(e.target.value)} disabled={!canEdit}
                    data-testid="card-due-input"
                    className="bg-white border-2 border-black px-2 py-1 font-mono text-xs uppercase tracking-wider" />
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="font-mono text-[10px] uppercase tracking-widest flex items-center gap-1">
                  <AlignLeft size={11} strokeWidth={2.5} /> Deskripsi
                </div>
                {savingDesc && <span className="font-mono text-[9px] uppercase tracking-widest text-black/50 animate-pulse">menyimpan…</span>}
              </div>
              {canEdit ? (
                <RichTextEditor value={description} onChange={onDescChange} placeholder="Tulis konteks, detail, atau langkah…" />
              ) : (
                <div className="border-2 border-black p-3 md-render" dangerouslySetInnerHTML={{ __html: description || "<p class='text-black/40'>—</p>" }} />
              )}
            </div>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-widest flex items-center gap-1">
                  <ImageIcon size={11} strokeWidth={2.5} /> Lampiran ({attachments.length})
                </div>
                {canEdit && (
                  <div className="grid grid-cols-2 sm:flex sm:items-center gap-1 w-full sm:w-auto">
                    <button onClick={() => camRef.current?.click()} data-testid="upload-camera-btn" title="Kamera"
                      className="bg-white border border-black px-2 py-2 sm:py-1 font-mono text-[10px] uppercase tracking-widest btn-brut hover:bg-canary hover:-translate-y-0.5 hover:shadow-brut">
                      <Camera size={11} className="inline mr-1" strokeWidth={2.5} /> Kamera
                    </button>
                    <button onClick={() => fileRef.current?.click()} data-testid="upload-file-btn" title="Unggah"
                      className="bg-klein text-white border border-black px-2 py-2 sm:py-1 font-mono text-[10px] uppercase tracking-widest btn-brut hover:bg-black hover:-translate-y-0.5 hover:shadow-brut">
                      <Upload size={11} className="inline mr-1" strokeWidth={2.5} /> Unggah
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files?.[0])} data-testid="file-input" />
                    <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
                  </div>
                )}
              </div>
              {uploadingFile && (
                <div className="flex items-center gap-3 border-2 border-black bg-canary/30 px-3 py-2 mb-2" data-testid="upload-progress-banner">
                  <UploadProgress progress={uploadProgress} status={uploadStatus} size={36} />
                  <div className="flex-1 font-mono text-[10px] uppercase tracking-widest">
                    {uploadStatus === "success" ? "Berhasil diunggah" : uploadStatus === "error" ? "Gagal — mencoba ulang…" : "Mengunggah ke ImgBB…"}
                  </div>
                </div>
              )}
              {attachments.length === 0 ? (
                <div className="border-2 border-dashed border-black/40 p-6 text-center font-mono text-xs text-black/50">
                  Seret-jatuhkan gambar di sini, atau klik Unggah / Kamera.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="attachments-gallery">
                  {attachments.map((a) => {
                    const isCover = a.attachment_id === coverId;
                    return (
                      <div key={a.attachment_id} className="group relative border-2 border-black aspect-square overflow-hidden"
                        data-testid={`attachment-${a.attachment_id}`}>
                        <img src={a.thumb_url || a.url} alt=""
                          onClick={() => onOpenLightbox && onOpenLightbox(a.url)}
                          className="w-full h-full object-cover cursor-zoom-in" />
                        {isCover && (
                          <div className="absolute top-1 left-1 bg-canary text-black border border-black font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 flex items-center gap-1">
                            <Star size={8} fill="#000" strokeWidth={2.5} /> COVER
                          </div>
                        )}
                        {canEdit && (
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 p-2">
                            <button onClick={() => setCover(isCover ? null : a.attachment_id)}
                              data-testid={`set-cover-${a.attachment_id}`}
                              className="bg-white text-black border border-white px-2 py-1 font-mono text-[9px] uppercase tracking-widest btn-brut hover:bg-canary">
                              {isCover ? "Hapus Cover" : "Jadikan Cover"}
                            </button>
                            <button onClick={() => deleteAttachment(a.attachment_id)}
                              className="bg-signal text-white border border-white px-2 py-1 font-mono text-[9px] uppercase tracking-widest btn-brut">
                              <Trash2 size={10} className="inline" strokeWidth={2.5} /> Hapus
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Comments */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest mb-2 flex items-center gap-1">
                <MessageSquare size={11} strokeWidth={2.5} /> Komentar ({comments.length})
              </div>
              <div className="space-y-3">
                {comments.map((c) => (
                  <div key={c.comment_id} className="flex gap-2 border border-black p-3 bg-white" data-testid={`comment-${c.comment_id}`}>
                    <Avatar user={c.user} size={28} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs">{c.user?.name}</span>
                          <span className="font-mono text-[9px] uppercase tracking-widest text-black/50">
                            {new Date(c.created_at).toLocaleString("id-ID")}
                          </span>
                        </div>
                        <button onClick={() => deleteComment(c.comment_id)} className="text-black/40 hover:text-signal" title="Hapus">
                          <Trash2 size={11} strokeWidth={2.5} />
                        </button>
                      </div>
                      <div className="md-render text-sm mt-1" dangerouslySetInnerHTML={{ __html: c.body }} />
                    </div>
                  </div>
                ))}
                {canEdit && (
                  <div>
                    <RichTextEditor value={commentBody} onChange={setCommentBody} placeholder="Tulis komentar… (Bold/Bullet/Numbered didukung)" compact />
                    <div className="flex justify-end mt-2">
                      <button onClick={addComment} disabled={!commentBody.trim() || commentBody === "<p></p>"}
                        data-testid="submit-comment"
                        className="bg-klein text-white border-2 border-black px-4 py-2 font-mono uppercase text-xs tracking-wider btn-brut hover:bg-black hover:-translate-y-0.5 hover:shadow-brut disabled:opacity-40">
                        Kirim Komentar →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <aside className="border-t-2 md:border-t-0 md:border-l-2 border-black p-3 md:p-4 bg-[#F9F9F9] space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-black/60">AKSI</div>

            {canEdit && (
              <>
                <SidebarButton icon={<Tag size={12} strokeWidth={2.5} />} label="Label" onClick={() => setShowLabelsPicker(true)} testid="open-labels-picker" />
                <SidebarButton icon={<Users size={12} strokeWidth={2.5} />} label="Anggota" onClick={() => setShowMembersPicker(true)} testid="open-members-picker" />
                <SidebarButton icon={<Calendar size={12} strokeWidth={2.5} />} label="Tenggat" onClick={() => {
                  const v = window.prompt("Tanggal (YYYY-MM-DD)", dueDate);
                  if (v !== null) saveDueDate(v);
                }} />
                <SidebarButton icon={<ImageIcon size={12} strokeWidth={2.5} />} label="Lampiran" onClick={() => fileRef.current?.click()} />
              </>
            )}

            <div className="pt-3 border-t border-black space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-black/60">META</div>
              <div className="font-mono text-[10px] text-black/60">
                Dibuat: {new Date(card.created_at).toLocaleDateString("id-ID")}
              </div>
              {(myRole === "spv" || myRole === "master" || card.created_by === card.created_by_actor) && (
                <button onClick={deleteCard} data-testid="delete-card-btn"
                  className="w-full bg-white text-black border-2 border-black px-3 py-2 font-mono uppercase text-[10px] tracking-wider btn-brut hover:bg-signal hover:text-white hover:-translate-y-0.5 hover:shadow-brut flex items-center justify-center gap-2">
                  <Trash2 size={11} strokeWidth={2.5} /> Hapus Kartu
                </button>
              )}
            </div>
          </aside>
        </div>

        {showLabelsPicker && (
          <LabelsPicker labels={labels} selected={cardLabels} onToggle={toggleLabel} onClose={() => setShowLabelsPicker(false)} />
        )}
        {showMembersPicker && (
          <MembersPicker members={members} selected={cardMembers.map((m) => m.user_id)} onToggle={toggleMember} onClose={() => setShowMembersPicker(false)} />
        )}
      </div>
    </div>
  );
}

function SidebarButton({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-2 bg-white border border-black px-3 py-2 font-mono uppercase text-[10px] tracking-widest text-left btn-brut hover:bg-canary hover:-translate-y-0.5 hover:shadow-brut">
      {icon} {label}
    </button>
  );
}

function Avatar({ user, size = 32, title }) {
  if (!user) return null;
  return user.picture ? (
    <img src={user.picture} alt="" title={title || user.name} style={{ width: size, height: size }}
      className="border-2 border-white object-cover" />
  ) : (
    <div style={{ width: size, height: size }} title={title || user.name}
      className="bg-canary border-2 border-white flex items-center justify-center font-mono text-[10px] font-bold">
      {(user.name || "?").slice(0, 2).toUpperCase()}
    </div>
  );
}

function LabelsPicker({ labels, selected, onToggle, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fadeIn">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white border-2 border-black shadow-brutLg animate-blockIn">
        <div className="border-b-2 border-black p-4 bg-canary flex items-center justify-between">
          <h3 className="font-heading font-black text-lg uppercase tracking-tight">Pilih Label</h3>
          <button onClick={onClose} className="w-8 h-8 border border-black bg-white hover:bg-black hover:text-white"><X size={14} className="mx-auto" strokeWidth={2.5} /></button>
        </div>
        <div className="p-3 max-h-80 overflow-y-auto space-y-1">
          {labels.map((l) => {
            const active = selected.includes(l.label_id);
            return (
              <button key={l.label_id} onClick={() => onToggle(l.label_id)}
                data-testid={`toggle-label-${l.label_id}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 border-2 font-mono text-xs uppercase tracking-wider btn-brut ${active ? "border-black shadow-brut" : "border-black/20 hover:border-black"}`}
                style={{ background: l.color, color: getContrast(l.color) }}>
                <span>{l.name}</span>
                {active && <Check size={14} strokeWidth={3} />}
              </button>
            );
          })}
          {labels.length === 0 && <div className="font-mono text-xs text-black/50 text-center py-4">Belum ada label. Buat di Pengaturan Papan.</div>}
        </div>
      </div>
    </div>
  );
}

function MembersPicker({ members, selected, onToggle, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 animate-fadeIn">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-white border-2 border-black shadow-brutLg animate-blockIn">
        <div className="border-b-2 border-black p-4 bg-klein text-white flex items-center justify-between">
          <h3 className="font-heading font-black text-lg uppercase tracking-tight">Pilih Anggota</h3>
          <button onClick={onClose} className="w-8 h-8 border border-white bg-transparent hover:bg-white hover:text-black"><X size={14} className="mx-auto" strokeWidth={2.5} /></button>
        </div>
        <div className="p-3 max-h-80 overflow-y-auto space-y-1">
          {members.map((m) => {
            const active = selected.includes(m.user_id);
            return (
              <button key={m.user_id} onClick={() => onToggle(m.user_id)}
                data-testid={`toggle-member-${m.user_id}`}
                className={`w-full flex items-center gap-3 px-3 py-2 border-2 btn-brut ${active ? "border-black bg-canary shadow-brut" : "border-black/20 hover:border-black bg-white"}`}>
                <Avatar user={m.user} size={28} />
                <div className="flex-1 text-left min-w-0">
                  <div className="font-bold text-sm truncate">{m.user?.name}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-black/50">{m.role}</div>
                </div>
                {active && <Check size={14} strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getContrast(hex) {
  if (!hex) return "#000";
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16), g = parseInt(c.slice(2, 4), 16), b = parseInt(c.slice(4, 6), 16);
  return (r * 0.299 + g * 0.587 + b * 0.114) > 170 ? "#000" : "#fff";
}
